import { Component, OnDestroy, OnInit, Inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule, ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { ModernCircularProgressComponent } from '../../shared/modern-circular-progress/modern-circular-progress.component';
import { of, forkJoin } from 'rxjs';
import { catchError, finalize, first, tap, map } from 'rxjs/operators';
import { ROUTE_DRIVER_JOB, ROUTE_DRIVER_JOB_APPLY } from 'src/app/driver-job/shared/routes';
import { FrameNavigationService } from 'src/app/frame/shared/frame-navigation.service';
import { Action } from 'src/app/jobs-shared/job-data/models/action';
import { ProfileAction } from 'src/app/profile/shared/profile-action.model';
import { ConfirmationDialogComponent } from 'src/app/shared/dialog/confitmation-dialog/confirmation-dialog.component';
import { Driver } from 'src/app/shared/models/driver.model';
import { Employment } from 'src/app/shared/models/employment.model';
import { License } from 'src/app/shared/models/license.model';
import { ProfilePicture } from 'src/app/shared/models/profile-picture.model';
import { PublicCarrier } from 'src/app/shared/models/public-carrier';
import {
  ROUTE_HELP_AND_SUPPORT,
  ROUTE_PERSONAL_INFO,
  ROUTE_PROFILE,
  ROUTE_RESIDENCE_HISTORY,
  ROUTE_DQF,
  ROUTE_DQF_DOCUMENT_UPLOAD,
  ROUTE_CARRIER_COMPANY,
  ROUTE_DRIVER_RISK_PROFILE,
  ROUTE_ORDER_REPORTS,
  ROUTE_JOB_BOARD
} from 'src/app/shared/routes';
import { ROUTE_CARRIER_COMPANY_MENU } from 'src/app/carrier-company/shared/routes';
import { CarrierDataService } from 'src/app/shared/services/carrier-data.service';
import { DriverQualificationService } from 'src/app/driver-qualification-files/service/driver-qualification.service';
import { CompleteProfileSheetComponent } from 'src/app/driver-qualification-files/complete-profile-sheet/complete-profile-sheet.component';
import { SubSink } from 'subsink';
import { RegistrationService } from '../shared/registration.service';
import { CompanyContact, formatCompanyContactTypeLabel, getDriverVisibleCompanyContacts, whatsappNumberForUrl } from 'src/app/shared/models/company-contact';
import { JOB_APPLY_ORIGIN_JOB_BOARD, JOB_APPLY_ORIGIN_KEY } from 'src/app/job-board/shared/models/job-board.model';
import { CompanyInterestService } from '../shared/company-interest.service';
const DISABLE_TOP_LOADING_BAR = true;
const DEFAULT_PROFILE_IMAGE = '/assets/images/default-profile-picture.png';
const DEFAULT_COMPANY_LOGO = '/assets/images/default-profile.png';
const ENVIRONMENTS = {
  DEV: 'dev',
  LOCAL: 'local',
  TEST: 'test',
  DEMO: 'demo',
  PROD: 'prod'
};
const STRIPE_URLS: Record<string, string> = {
  [ENVIRONMENTS.DEV]: 'https://buy.stripe.com/test_8x24gz7EsfhHgbYbtVcjS00',
  [ENVIRONMENTS.TEST]: 'https://buy.stripe.com/test_4gMaEWdGJbXuaIBcOag3600',
  [ENVIRONMENTS.DEMO]: 'https://buy.stripe.com/aFa8wP2ta5eWdhP6A0bjW04',
  [ENVIRONMENTS.PROD]: 'https://buy.stripe.com/aFa8wP2ta5eWdhP6A0bjW04'
};

// DQF Status Constants
const DQF_STATUS_CREATED = 'Created';
const DQF_STATUS_REJECTED = 'Rejected';
const DQF_STATUS_PROCESSING = 'Processing';
const DQF_STATUS_PENDINGAPPROVAL = 'PendingCarrierApproval';
const DQF_STATUS_DRIVER_SIGNATURE_SUBMITED = 'DriverSignatureSubmitted';
const DQF_STATUS_APPROVED = 'Approved';
const DQF_STATUS_CARRIERPROCESSINGCOMPLETED = 'CarrierProcessingCompleted';
const DQF_STATUS_APPROVEDPROCESSING = 'ApprovedProcessing';
const DQF_STATUS_WITHDRAWN = 'Withdrawn';
const DQF_STATUS_FINALLY_REJECTED = 'FinallyRejected';
const DQF_STATUS_TERMINATED = 'Terminated';
const STATUS_CODE = 200;
const PROFILE_STATUS_INCOMPLETE = 'Incomplete';

// Hiring Decision Status
const HIRING_DECISION_PENDING = 'pending';
const HIRING_DECISION_HIRED = 'hired';
const HIRING_DECISION_NOT_HIRED = 'notHired';

// Carrier Association Card Interface
interface CarrierAssociationCard {
  carrierCompanyId: string;
  carrierCompanyName: string;
  carrierDbaName?: string;
  carrierCompanyLogo: string;
  hiredStatus: string;
  driverType: string;
  hasDqf: boolean;
  dqfStatus?: string;
  dqfId?: string;
  modifiedDate?: Date;
  employmentStartDate?: string;
  driverVisibleContacts?: CompanyContact[];
  contactsExpanded?: boolean;
}

@Component({
  selector: 'app-driver-profile-dashboard',
  templateUrl: './driver-profile-dashboard.component.html',
  styleUrls: ['./driver-profile-dashboard.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ModernCircularProgressComponent
  ],
  providers: [DatePipe]
})
export class DriverProfileDashboardComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  isProfileContentReady: boolean = false;
  profilePicture: ProfilePicture = new ProfilePicture();
  loading: boolean;
  driverName: string;
  driverEmail: string;
  driverId: string;
  profilePicturePath: any;
  requiredInformation: ProfileAction[] = [];
  additionalInformation: ProfileAction[] = [];
  licenseDetails: License;
  showPercentage = 0;
  requiredCompleted = 0;
  additionalRequired = 0;
  driverStatus: string;
  isRequireInfoReady: boolean = false;
  profileSectionCompletionCount: number;
  totalRequiredProfileSectionCount: number = 7;
  driverFullName: string;
  carrierAssociations: number;
  mode: ProgressSpinnerMode = 'determinate';
  driverGender: string;
  driverDob: string | Date;
  driver: Driver;
  carrierData: PublicCarrier = null;
  
  // DQF properties
  dqfs: any[] = [];
  allDqfs: any;
  
  // Carrier Association properties
  carrierAssociationCards: CarrierAssociationCard[] = [];
  private employmentHistory: Employment[] = [];

  // Job Board: hidden only when any carrier association is hired.
  showJobBoardCard = false;
  jobBoardCountryLabel = 'Showing Canadian jobs';

  constructor(
    private readonly registrationService: RegistrationService,
    private readonly frameNavigationService: FrameNavigationService,
    private readonly router: Router,
    private readonly carrierDataService: CarrierDataService,
    private readonly dialog: MatDialog,
    private readonly driverQualificationService: DriverQualificationService,
    private readonly bottomSheet: MatBottomSheet,
    private readonly companyInterestService: CompanyInterestService,
    private readonly datePipe: DatePipe,
    @Inject('environmentData') public environment: any
  ) {
  }

  ngOnInit(): void {
    this.subs.sink = this.registrationService.loadDriver(DISABLE_TOP_LOADING_BAR).subscribe((response: Driver) => {
      if (response) {
        this.employmentHistory = (response as Driver & { employment?: Employment[] }).employment || [];
        this.loadProfileImage();
        this.getLicense(response);
        this.loadCarrierAssociations();
      }
    });
    const orderPccOutsideDqf = sessionStorage.getItem('orderPccOutsideDqf');
    if (orderPccOutsideDqf) {
      sessionStorage.removeItem('orderPccOutsideDqf');
    }
    sessionStorage.removeItem('postStripeReturnRoute');
    sessionStorage.removeItem('stripeCheckoutSessionResponse');
    sessionStorage.removeItem('postStripeReturnRouteOutsideDqf');
    sessionStorage.removeItem('pccReportData');
  }

  ngOnDestroy() {
    const pccDataExists = sessionStorage.getItem('pccReportData');
    if (pccDataExists) {
      sessionStorage.removeItem('pccReportData');
    }
  }

  onNavigateToProfile() {
    this.registrationService.navigateToProfile();
  }

  onNavigateToHelpAndSupport() {
    this.router.navigate([ROUTE_HELP_AND_SUPPORT]);
  }

  navigateToDriverQualification() {
    this.frameNavigationService.navigateToDriverQualificaton();
  }

  navigateToReferFriend() {
    this.frameNavigationService.navigateToReferFriend();
  }

  navigateToMyDocuments() {
    this.frameNavigationService.navigateToMyDocuments();
  }

  navigateToRiskProfile() {
    this.router.navigate([ROUTE_DRIVER_RISK_PROFILE]);
  }

  navigateToJobBoard() {
    this.router.navigate([`/${ROUTE_JOB_BOARD}`]);
  }

  orderPcc() {
    sessionStorage.setItem('orderPccOutsideDqf', 'true');

    const isMissingPersonalInfo =
      !this.driverFullName?.trim() || !this.driverDob || !['male', 'female'].includes(this.driverGender?.toLowerCase());
    if (isMissingPersonalInfo) {
      this.router.navigate([ROUTE_PERSONAL_INFO]);
    } else if (!this.driver.residenceHistoryRequirementsCompleted) {
      this.router.navigate([ROUTE_RESIDENCE_HISTORY]);
    } else {
      this.router.navigate(['/driverQualification/birthLocation']);
    }
  }

  orderAbstract(): void {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];

    let environment: string;

    if (subdomain.startsWith(`${ENVIRONMENTS.DEV}-`) || subdomain.startsWith(`${ENVIRONMENTS.LOCAL}-`)) {
      environment = ENVIRONMENTS.DEV;
    } else if (subdomain.startsWith(`${ENVIRONMENTS.TEST}-`)) {
      environment = ENVIRONMENTS.TEST;
    } else if (subdomain.startsWith(`${ENVIRONMENTS.DEMO}-`)) {
      environment = ENVIRONMENTS.DEMO;
    } else if (subdomain === 'driver') {
      environment = ENVIRONMENTS.PROD;
    } else {
      environment = ENVIRONMENTS.PROD;
    }

    const baseUrl = STRIPE_URLS[environment];
    const targetUrl = `${baseUrl}?prefilled_email=${this.driverEmail}`;

    window.open(targetUrl, '_blank');
  }

  /**
   * Dispatcher for the "Order Abstracts" tile on the dashboard.
   *
   * When the demo-only `enableAbstractTelematicsBundle` feature flag is on
   * (local + demo environments), the tile label becomes
   * "Order Abstracts + Telematics" and routes to the reports catalog screen
   * where the driver picks between Abstract, Abstract + Telematics (bundle),
   * or Telematics Experience only.
   *
   * In every other environment this preserves the existing behavior (open
   * the Stripe abstract checkout directly).
   */
  onAbstractTileClick(): void {
    if (this.environment?.featureFlags?.enableAbstractTelematicsBundle) {
      this.router.navigate([`/${ROUTE_ORDER_REPORTS}`]);
      return;
    }
    this.orderAbstract();
  }

  private loadProfileImage(): void {
    this.registrationService
      .loadProfileImageThumbnail(DISABLE_TOP_LOADING_BAR)
      .pipe(
        catchError((error: any) => {
          this.isProfileContentReady = true;
          return of(false);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((res: any) => {
        if (res && res.fileUrl) {
          this.profilePicturePath = res.fileUrl;
        } else {
          this.profilePicturePath = DEFAULT_PROFILE_IMAGE;
        }
        this.isProfileContentReady = true;
      });
  }
  private getLicense(driverResponse): void {
    const license: any = this.registrationService.getLicenseStore();
    if (license) {
      this.licenseDetails = license;
    }
    this.bindData(driverResponse);
  }

  private bindData(response: Driver): void {
    this.driver = response;
    this.driverName = `${response.firstName}`;
    this.driverFullName = `${response.firstName} ${response.lastName}`;
    this.driverGender = `${response.gender}`;
    this.driverEmail = response.email;
    this.driverDob = response.birthday;
    this.driverId = response.driverID;
    this.requiredInformation = this.getCompletedSections(response);
    const completeValues = this.requiredInformation.filter(element => element.isComplete);
    this.showPercentage = +((+completeValues?.length / this.totalRequiredProfileSectionCount) * 100).toFixed(0);
    this.requiredCompleted = completeValues?.length;
    this.driverStatus = response.identityStatus;
    this.isRequireInfoReady = true;
    this.carrierAssociations = response?.companyAssociations?.length;
    this.showJobBoardCard = !(response?.companyAssociations || []).some(
      (a: any) => a.hiredStatus === HIRING_DECISION_HIRED
    );
    this.jobBoardCountryLabel = (response?.country || '').toLowerCase().includes('us')
      ? 'Showing US jobs'
      : 'Showing Canadian jobs';
    const carrierCompanyId = sessionStorage.getItem('carrierCompanyId');
    const jobApplyOrigin = sessionStorage.getItem(JOB_APPLY_ORIGIN_KEY);

    if (jobApplyOrigin === JOB_APPLY_ORIGIN_JOB_BOARD) {
      // Applies started from the Job Board reuse the carrier apply form but must
      // not re-trigger the carrier invite-link gating/redirect on the dashboard.
      sessionStorage.removeItem(JOB_APPLY_ORIGIN_KEY);
      sessionStorage.removeItem('carrierCompanyId');
      sessionStorage.removeItem('publishableKey');
    } else if (carrierCompanyId) {
      const hasCarrierAssociation = response.companyAssociations?.some(
        association => association.id === carrierCompanyId
      );

      this.carrierDataService.getCarrierPublicById(carrierCompanyId).subscribe({
        next: carrierResponse => {
          this.carrierData = carrierResponse;

          const isDriverApplyEnabled = this.carrierData?.driverApplyFeatureEnabled;

          if (isDriverApplyEnabled === true) {
            if (!hasCarrierAssociation && this.showPercentage === 100) {
              this.router.navigate([`/${ROUTE_DRIVER_JOB}`, ROUTE_DRIVER_JOB_APPLY]);
            } else if (!hasCarrierAssociation && this.showPercentage < 100) {
              this.showIncompleteProfileDialog();
            }
          }
        },
        error: err => {
          console.error('Error fetching carrier data', err);
        }
      });
    }

    this.openPendingInterestsDialogIfEligible();
  }

  private openPendingInterestsDialogIfEligible(): void {
    if (this.companyInterestService.isActiveInviteApplyFlow()) {
      return;
    }

    setTimeout(() => {
      this.subs.sink = this.companyInterestService.openPendingInterestsDialogOnDashboard().subscribe();
    }, 0);
  }

  private getCompletedSections(driverInfo: Driver): any[] {
    return [
      {
        name: 'Personal Profile',
        isComplete: this.isPersonalProfileComplete(driverInfo)
      },
      {
        name: 'Employment History',
        isComplete: !!driverInfo.employmentRequirementsCompleted
      },
      {
        name: 'License',
        isComplete: !!driverInfo.licenseRequirementsCompleted
      },
      {
        name: 'Residence History',
        isComplete: !!driverInfo.residenceHistoryRequirementsCompleted
      },
      {
        name: 'Driving Experience',
        isComplete: !!driverInfo.experienceRequirementsCompleted
      },
      {
        name: 'Accident Records',
        isComplete: !!driverInfo.accidentsRequirementsCompleted
      },
      {
        name: 'Traffic Convictions',
        isComplete: !!driverInfo.convictionsRequirementsCompleted
      }
    ];
  }

  private isPersonalProfileComplete(driverInfo: Driver): boolean {
    if (!driverInfo) {
      return false;
    }
    const { firstName, lastName, birthday, phoneNumber, gender, address, emergencyContacts } = driverInfo;
    if (!firstName || !lastName || !birthday || !phoneNumber || !gender || !address) {
      return false;
    }
    if (!emergencyContacts || emergencyContacts.length === 0) {
      return false;
    }
    return true;
  }

  private showIncompleteProfileDialog(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      disableClose: true,
      data: {
        title: '🛑 Application Not Submitted',
        message: '❌ Your application has not been submitted.',
        subMessage: 'Please complete your profile, then click the job application link again to apply.',
        actions: []
      }
    });

    const actions = this.createOkDialogActions(dialogRef);
    dialogRef.componentInstance.data.actions = actions;  

    dialogRef.afterClosed().subscribe();
  }

  private createOkDialogActions(dialogRef: MatDialogRef<ConfirmationDialogComponent>): Action[] {
    const okAction: Action = {
      name: 'OK',
      action: () => {
        // sessionStorage.removeItem('carrierCompanyId');
        // sessionStorage.removeItem('publishableKey');
        dialogRef.close();
        this.router.navigate([`/${ROUTE_PROFILE}`]);
        return {};
      },
      disabled: false,
      color: 'primary'
    };

    return [okAction];
  }

  // Carrier Association Methods
  private loadCarrierAssociations(): void {
    const driver = this.registrationService.getDriver();
    const companyAssociations = driver?.companyAssociations;
    
    if (!companyAssociations || companyAssociations.length === 0) {
      this.carrierAssociationCards = [];
      return;
    }

    // Fetch DQFs if feature is enabled
    if (this.environment?.featureFlags?.isDQFEnable) {
      this.subs.add(
        this.driverQualificationService
          .getDqfs()
          .pipe(
            catchError((error: any) => {
              return of([]);
            })
          )
          .subscribe(dqfs => {
            this.allDqfs = dqfs && dqfs.length > 0 ? this.registrationService.getDqfsWithDriverType(dqfs) : [];
            this.dqfs = this.allDqfs; // Keep for backward compatibility
            this.buildCarrierAssociationCards(companyAssociations, this.allDqfs);
          })
      );
    } else {
      // If DQF feature is disabled, just build cards from associations
      this.buildCarrierAssociationCards(companyAssociations, []);
    }
  }

    /**
   * When multiple DQFs exist for the same carrier, pick the one that should drive the card
   * (e.g. a new Created DQF over an older Approved one).
   */
    private pickRepresentativeDqfForCarrier(carrierDqfs: any[]): any | undefined {
      if (!carrierDqfs?.length) {
        return undefined;
      }
      const newestFirst = (a: any, b: any) => {
        const ta = new Date(a.modifiedDate || a.createdAt || 0).getTime();
        const tb = new Date(b.modifiedDate || b.createdAt || 0).getTime();
        return tb - ta;
      };
      const takeNewest = (list: any[]) => [...list].sort(newestFirst)[0];
  
      const incomplete = carrierDqfs.filter(
        d => d.status === DQF_STATUS_CREATED || d.status === DQF_STATUS_PROCESSING
      );
      if (incomplete.length) {
        return takeNewest(incomplete);
      }
      const pending = carrierDqfs.filter(
        d =>
          d.status === DQF_STATUS_PENDINGAPPROVAL || d.status === DQF_STATUS_DRIVER_SIGNATURE_SUBMITED
      );
      if (pending.length) {
        return takeNewest(pending);
      }
      const rejected = carrierDqfs.filter(d => d.status === DQF_STATUS_REJECTED);
      if (rejected.length) {
        return takeNewest(rejected);
      }
      const approved = carrierDqfs.filter(d =>
        [DQF_STATUS_APPROVED, DQF_STATUS_CARRIERPROCESSINGCOMPLETED, DQF_STATUS_APPROVEDPROCESSING].includes(
          d.status
        )
      );
      if (approved.length) {
        return takeNewest(approved);
      }
      const withdrawn = carrierDqfs.filter(d => d.status === DQF_STATUS_WITHDRAWN);
      if (withdrawn.length) {
        return takeNewest(withdrawn);
      }
      return takeNewest(carrierDqfs);
    }

  private buildCarrierAssociationCards(companyAssociations: any[], dqfs: any[]): void {
    // One carrier may have multiple DQFs; pick the DQF that should drive UI (e.g. Created over Approved)
    const dqfsByCarrier = new Map<string, any[]>();
    dqfs.forEach(dqf => {
      const carrierId = dqf.carrierCompanyId;
      if (!dqfsByCarrier.has(carrierId)) {
        dqfsByCarrier.set(carrierId, []);
      }
      dqfsByCarrier.get(carrierId)!.push(dqf);
    });
    const dqfMap = new Map<string, any>();
    dqfsByCarrier.forEach((list, carrierId) => {
      dqfMap.set(carrierId, this.pickRepresentativeDqfForCarrier(list));
    });

    // Build carrier association cards from all associations
    this.carrierAssociationCards = companyAssociations.map(association => {
      const dqf = dqfMap.get(association.id);
      
      return {
        carrierCompanyId: association.id,
        carrierCompanyName: dqf?.carrierCompanyName || '', // Will be fetched later
        carrierCompanyLogo: DEFAULT_COMPANY_LOGO,
        hiredStatus: association.hiredStatus || HIRING_DECISION_PENDING,
        driverType: association.driverType || dqf?.driverType || '',
        hasDqf: !!dqf,
        dqfStatus: dqf?.status,
        dqfId: dqf?.id,
        modifiedDate: dqf?.modifiedDate ? new Date(dqf.modifiedDate) : new Date()
      };
    })
    // Filter out pending cards (not hired + no DQF)
    .filter(card => {
      // Hide carriers entirely when the latest DQF is in terminal states.
      if (this.isHiddenDqfStatus(card.dqfStatus)) {
        return false;
      }
      // Keep the card if driver is hired OR card has a DQF
      return card.hiredStatus === HIRING_DECISION_HIRED || card.hasDqf;
    });

    // Sort carrier association cards by priority
    this.carrierAssociationCards.sort((a, b) => {
      // Priority order: Hired (Active) → Incomplete DQF → Pending DQF → Approved not hired → Rejected → Others
      const getPriority = (card: CarrierAssociationCard) => {
        // Highest priority: Hired drivers (Active)
        if (card.hiredStatus === HIRING_DECISION_HIRED) {
          return 1;
        }
        
        // Second priority: Incomplete/Created DQF (Submit DQF)
        if (card.hasDqf && (card.dqfStatus === DQF_STATUS_CREATED || card.dqfStatus === DQF_STATUS_PROCESSING)) {
          return 2;
        }
        
        // Third priority: Pending DQF (Under Review)
        if (card.hasDqf && (card.dqfStatus === DQF_STATUS_PENDINGAPPROVAL || card.dqfStatus === DQF_STATUS_DRIVER_SIGNATURE_SUBMITED)) {
          return 3;
        }
        
        // Fourth priority: Approved DQF but not hired
        if (card.hasDqf && this.isApproved(card.dqfStatus)) {
          return 4;
        }
        
        // Fifth priority: Rejected DQF
        if (card.hasDqf && card.dqfStatus === DQF_STATUS_REJECTED) {
          return 5;
        }
        
        // Sixth priority: Withdrawn
        if (card.hasDqf && card.dqfStatus === DQF_STATUS_WITHDRAWN) {
          return 6;
        }
        
        // Seventh priority: Association without DQF (pending)
        if (!card.hasDqf && card.hiredStatus === HIRING_DECISION_PENDING) {
          return 7;
        }
        
        // Lowest priority: Not hired
        return 8;
      };
      
      const priorityA = getPriority(a);
      const priorityB = getPriority(b);
      
      // Sort by priority first
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // If same priority, sort by modified date (newest first)
      return b.modifiedDate.getTime() - a.modifiedDate.getTime();
    });

    // Fetch carrier company details (names and logos)
    this.fetchCarrierCompanyDetails();
  }

  private fetchCarrierCompanyDetails(): void {
    if (this.carrierAssociationCards.length === 0) {
      return;
    }

    // Create array of carrier detail fetch calls
    const carrierDetailCalls = this.carrierAssociationCards.map(card => 
      this.carrierDataService.getCarrierPublicById(card.carrierCompanyId).pipe(
        map((carrier: PublicCarrier) => ({
          id: card.carrierCompanyId,
          name: carrier.companyName,
          dbaName: carrier.dbaName
        })),
        catchError(() => of({
          id: card.carrierCompanyId,
          name: 'Unknown Carrier',
          dbaName: ''
        }))
      )
    );

    forkJoin(carrierDetailCalls)
      .pipe(
        first(),
        tap(carrierDetails => {
          // Update carrier names
          this.carrierAssociationCards.forEach(card => {
            const details = carrierDetails.find((d: any) => d.id === card.carrierCompanyId);
            if (details?.name) {
              card.carrierCompanyName = details.name;
            }
            if (details) {
              card.carrierDbaName = details.dbaName || '';
            }
          });
          this.applyEmploymentStartDatesToActiveCards();
        })
      )
      .subscribe(() => {
        // Now fetch logos
        this.fetchCarrierCompanyLogos();
      });
  }

  private applyEmploymentStartDatesToActiveCards(): void {
    if (!this.employmentHistory.length) {
      return;
    }

    this.carrierAssociationCards.forEach(card => {
      if (!this.isHired(card.hiredStatus) || !card.carrierCompanyName) {
        card.employmentStartDate = undefined;
        return;
      }

      card.employmentStartDate = this.findEmploymentStartDateForCompany(
        card.carrierCompanyName,
        card.carrierDbaName
      );
    });
  }

  private findEmploymentStartDateForCompany(companyName: string, dbaName?: string): string | undefined {
    const matchingEmployments = this.employmentHistory.filter(employment => {
      if (employment.employmentState !== 'employed' || !employment.employer) {
        return false;
      }

      return (
        this.employerMatchesCompanyName(employment.employer, companyName) ||
        (!!dbaName && this.employerMatchesCompanyName(employment.employer, dbaName))
      );
    });

    if (!matchingEmployments.length) {
      return undefined;
    }

    const employment =
      matchingEmployments.find(record => record.isCurrentEmployment) ||
      [...matchingEmployments].sort(
        (a, b) => new Date(b.fromDate).getTime() - new Date(a.fromDate).getTime()
      )[0];

    return employment?.fromDate ? this.formatCardDate(employment.fromDate) : undefined;
  }

  private employerMatchesCompanyName(employer: string, companyName: string): boolean {
    const normalizedEmployer = this.normalizeCompanyName(employer);
    const normalizedCompany = this.normalizeCompanyName(companyName);

    if (!normalizedEmployer || !normalizedCompany) {
      return false;
    }

    return (
      normalizedEmployer === normalizedCompany ||
      normalizedEmployer.includes(normalizedCompany) ||
      normalizedCompany.includes(normalizedEmployer)
    );
  }

  private normalizeCompanyName(name: string): string {
    return (name || '').toLowerCase().trim().replace(/\s+/g, ' ');
  }

  private formatCardDate(date: string | Date): string {
    return this.datePipe.transform(date, 'MMMM dd, yyyy') || '';
  }

  getCardStatusDateParts(card: CarrierAssociationCard): { label: string; date: string } | null {
    if (this.isHired(card.hiredStatus)) {
      return card.employmentStartDate ? { label: 'Hired on:', date: card.employmentStartDate } : null;
    }

    if (!card.modifiedDate) {
      return null;
    }

    const formattedDate = this.formatCardDate(card.modifiedDate);

    if (this.isInComplete(card.dqfStatus)) {
      return { label: 'Pending since:', date: formattedDate };
    }

    if (this.isPending(card.dqfStatus)) {
      return { label: 'Submitted on:', date: formattedDate };
    }

    if (this.isApproved(card.dqfStatus)) {
      return { label: 'DQF Approved on:', date: formattedDate };
    }

    return null;
  }

  getCardStatusDateLine(card: CarrierAssociationCard): string | null {
    const parts = this.getCardStatusDateParts(card);
    return parts ? `${parts.label} ${parts.date}` : null;
  }

  shouldShowDbaName(card: CarrierAssociationCard): boolean {
    const dbaName = card.carrierDbaName?.trim();
    if (!dbaName) {
      return false;
    }

    const companyName = card.carrierCompanyName?.trim();
    return dbaName.toLowerCase() !== companyName?.toLowerCase();
  }

  getCardStatusDescription(card: CarrierAssociationCard): string | null {
    if (this.isHired(card.hiredStatus)) {
      return 'You currently work here. Click to see more options.';
    }

    if (!card.hasDqf) {
      return null;
    }

    if (this.isInComplete(card.dqfStatus)) {
      return 'Submit your DQF at the earliest to move the process forward. Click to see more options.';
    }

    if (this.isPending(card.dqfStatus)) {
      return 'Your DQF is under carrier review. Click to see more options.';
    }

    if (this.isApproved(card.dqfStatus)) {
      return 'Hiring decision is still pending, contact the company for any updates. Click to see more options.';
    }

    return null;
  }

  private fetchCarrierCompanyLogos(): void {
    const logoFetchCalls = this.carrierAssociationCards.map(card =>
      this.driverQualificationService.getCarrierCompanyLogo(card.carrierCompanyId).pipe(
        catchError(() =>
          of({ id: card.carrierCompanyId, companyLogo: null, companyContacts: [] as unknown[] })
        )
      )
    );

    forkJoin(logoFetchCalls)
      .pipe(
        first(),
        tap(responses => {
          // Assign logos and contacts to carrier association cards
          this.carrierAssociationCards.forEach(card => {
            const logoResponse = responses.find((res: any) => res.id === card.carrierCompanyId);

            if (logoResponse && logoResponse.companyLogo) {
              card.carrierCompanyLogo = logoResponse.companyLogo;
            } else {
              card.carrierCompanyLogo = DEFAULT_COMPANY_LOGO;
            }

            card.driverVisibleContacts = getDriverVisibleCompanyContacts(logoResponse?.companyContacts);
            card.contactsExpanded = false;
          });
        }),
        catchError((error: any) => {
          // Set default logo for all cards on error
          this.carrierAssociationCards.forEach(card => {
            card.carrierCompanyLogo = DEFAULT_COMPANY_LOGO;
          });
          return of(false);
        })
      )
      .subscribe();
  }

  isInComplete(status: string): boolean {
    return status == DQF_STATUS_CREATED || status == DQF_STATUS_PROCESSING;
  }

  isPending(status): boolean {
    return status == DQF_STATUS_PENDINGAPPROVAL || status == DQF_STATUS_DRIVER_SIGNATURE_SUBMITED;
  }

  isRejected(status): boolean {
    return status && status == DQF_STATUS_REJECTED;
  }

  isWithdrawn(status): boolean {
    return status && status == DQF_STATUS_WITHDRAWN;
  }

  isApproved(status): boolean {
    return (
      status &&
      [DQF_STATUS_APPROVED, DQF_STATUS_CARRIERPROCESSINGCOMPLETED, DQF_STATUS_APPROVEDPROCESSING].includes(status)
    );
  }

  private isHiddenDqfStatus(status?: string): boolean {
    return status === DQF_STATUS_FINALLY_REJECTED || status === DQF_STATUS_TERMINATED;
  }

  isHired(hiredStatus: string): boolean {
    return hiredStatus === HIRING_DECISION_HIRED;
  }

  isDriverHiredByCarrier(carrierCompanyId: string): boolean {
    const driver = this.registrationService.getDriver();
    if (!driver?.companyAssociations || !Array.isArray(driver.companyAssociations)) {
      return false;
    }
    
    const association = driver.companyAssociations.find(
      assoc => assoc.id === carrierCompanyId
    );
    
    return association?.hiredStatus === HIRING_DECISION_HIRED;
  }

  onCarrierCardClick(card: CarrierAssociationCard): void {
    // If card has DQF, use DQF-based logic
    if (card.hasDqf && card.dqfId) {
      this.onDqfItemClick({
        id: card.dqfId,
        status: card.dqfStatus,
        carrierCompanyId: card.carrierCompanyId,
        carrierCompanyName: card.carrierCompanyName,
        carrierCompanyLogo: card.carrierCompanyLogo
      });
    } else {
      // For carriers without DQF (e.g., ELD drivers), navigate to carrier menu
      if (card.hiredStatus === HIRING_DECISION_HIRED) {
        // Navigate to carrier company menu page for hired drivers
        this.router.navigate([`${ROUTE_CARRIER_COMPANY}/${ROUTE_CARRIER_COMPANY_MENU}/${card.carrierCompanyId}/no-dqf`], {
          state: {
            companyName: card.carrierCompanyName,
            companyLogo: card.carrierCompanyLogo || '',
            dqfStatus: null,
            isEldDriver: true
          }
        });
      }
      // For non-hired associations without DQF, do nothing
    }
  }

  onDqfItemClick(dqf): void {
    let id = dqf.id;
    if (dqf.status == DQF_STATUS_CREATED) {
      if (this.driver?.identityStatus === PROFILE_STATUS_INCOMPLETE) {
        let carrierName = dqf.carrierCompanyName;
        const dialogRef = this.bottomSheet.open(CompleteProfileSheetComponent, {
          panelClass: 'signature-panel',
          disableClose: true,
          data: { carrierName: carrierName }
        });
        dialogRef.afterDismissed().subscribe(res => {
          this.registrationService.navigateToProfile();
        });
      } else {
        let dqfStatus = {
          status: DQF_STATUS_PROCESSING
        };
        this.driverQualificationService.updateDqfStatus(id, dqfStatus).subscribe(res => {
          if (res && res.status === STATUS_CODE) {
            this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_DOCUMENT_UPLOAD}/${id}`]);
          }
        });
      }
    } else {
      // Navigate to carrier company menu page
      this.router.navigate([`${ROUTE_CARRIER_COMPANY}/${ROUTE_CARRIER_COMPANY_MENU}/${dqf.carrierCompanyId}/${dqf.id}`], {
        state: {
          companyName: dqf.carrierCompanyName,
          companyLogo: dqf.carrierCompanyLogo || '',
          dqfStatus: dqf.status
        }
      });
    }
  }

  toggleCardContactsExpanded(event: Event, card: CarrierAssociationCard): void {
    event.stopPropagation();
    card.contactsExpanded = !card.contactsExpanded;
  }

  contactTypeLabel(type: string): string {
    return formatCompanyContactTypeLabel(type);
  }

  whatsappHref(whatsApp: string): string {
    const digits = whatsappNumberForUrl(whatsApp);
    return digits ? `https://wa.me/${digits}` : '';
  }

  // Action tile click handlers
  onMyIncidentsClick(event: Event, dqf: any): void {
    event.stopPropagation(); // Prevent card click
    this.router.navigate(['/incidents/list', dqf.carrierCompanyId], {
      state: {
        companyId: dqf.carrierCompanyId,
        companyName: dqf.carrierCompanyName,
        dqfId: dqf.id
      }
    });
  }

  onGoToDqfClick(event: Event, dqf: any): void {
    event.stopPropagation(); // Prevent card click
    // Navigate to carrier company menu page
    this.router.navigate([`${ROUTE_CARRIER_COMPANY}/${ROUTE_CARRIER_COMPANY_MENU}/${dqf.carrierCompanyId}/${dqf.id}`], {
      state: {
        companyName: dqf.carrierCompanyName,
        companyLogo: dqf.carrierCompanyLogo || '',
        dqfStatus: dqf.status
      }
    });
  }
}
